class ClcNewHeader extends HTMLElement {
  static CART_DRAWER_OPEN = "fluco:modal:cartDrawer:open";

  constructor() {
    super();
    this.activeMegaId = null;
    this.isMobileOpen = false;
    this.desktopMediaQuery = window.matchMedia("(min-width: 990px)");
  }

  connectedCallback() {
    this.menuToggle = this.querySelector("[data-clc-header-menu-toggle]");
    this.mobilePanel = this.querySelector("[data-clc-header-mobile-panel]");
    this.overlay = this.querySelector("[data-clc-header-overlay]");
    this.megaTriggers = Array.from(this.querySelectorAll("[data-clc-header-mega-trigger]"));

    this.menuToggle?.addEventListener("click", () => this.toggleMobileMenu());
    this.overlay?.addEventListener("click", () => this.closeAllMenus());

    this.megaTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.toggleMegaMenu(trigger.dataset.clcHeaderMegaTrigger);
      });
    });

    this.querySelectorAll("[data-action='show-cart-drawer']").forEach((element) => {
      element.addEventListener("click", this.handleCartDrawerClick);
    });

    document.addEventListener("keydown", this.handleKeydown);
    this.desktopMediaQuery.addEventListener("change", () => this.closeAllMenus());
    window.addEventListener("resize", this.handleResize);

    this.updateHeaderMetrics();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleResize);
    this.unlockPageScroll();
  }

  handleKeydown = (event) => {
    if (event.key === "Escape") {
      this.closeAllMenus();
    }
  };

  handleCartDrawerClick = (event) => {
    event.preventDefault();
    this.closeAllMenus();
    window.dispatchEvent(new Event(ClcNewHeader.CART_DRAWER_OPEN));
  };

  handleResize = () => {
    this.updateHeaderMetrics();
  };

  updateHeaderMetrics = () => {
    const announcement = this.querySelector(
      ".clc-header__announcement:not(.clc-header__announcement--mobile-panel)"
    );
    const bar = this.querySelector(".clc-header__bar");
    const announcementHeight = announcement?.offsetHeight || 0;
    const barHeight = bar?.offsetHeight || 0;
    const visibleHeight = announcementHeight + barHeight;
    const totalHeight = this.offsetHeight;

    this.style.setProperty("--clc-header-visible-height", `${visibleHeight}px`);
    document.documentElement.style.setProperty("--header-height", `${totalHeight}px`);
    document.documentElement.style.setProperty("--header-height-internal", `${totalHeight}px`);
    this.style.setProperty("--clc-header-overlay-top", `${totalHeight}px`);
  };

  isDesktop() {
    return this.desktopMediaQuery.matches;
  }

  toggleMobileMenu() {
    if (this.isDesktop()) {
      return;
    }

    if (this.isMobileOpen) {
      this.closeAllMenus();
      return;
    }

    this.openMobileMenu();
  }

  openMobileMenu() {
    this.isMobileOpen = true;
    this.updateHeaderMetrics();
    this.lockPageScroll();
    this.classList.add("is-mobile-open");
    this.mobilePanel?.setAttribute("aria-hidden", "false");
    this.menuToggle?.setAttribute("aria-expanded", "true");
    this.menuToggle?.setAttribute(
      "aria-label",
      this.menuToggle.dataset.menuLabelClose || "Close menu"
    );
  }

  toggleMegaMenu(megaId) {
    if (!this.isDesktop() || !megaId) {
      return;
    }

    if (this.activeMegaId === megaId) {
      this.closeMegaMenu();
      return;
    }

    this.closeMegaMenu(false);
    this.activeMegaId = megaId;

    const panel = this.querySelector(`[data-clc-header-mega-panel="${megaId}"]`);
    const trigger = this.querySelector(`[data-clc-header-mega-trigger="${megaId}"]`);
    const navItem = trigger?.closest(".clc-header__nav-item");

    panel?.removeAttribute("hidden");
    trigger?.setAttribute("aria-expanded", "true");
    navItem?.classList.add("is-open");
    this.classList.add("is-mega-open");
    this.overlay?.removeAttribute("hidden");
    this.lockPageScroll();

    requestAnimationFrame(() => {
      this.updateHeaderMetrics();
    });
  }

  closeMegaMenu(unlockScroll = true) {
    if (!this.activeMegaId) {
      return;
    }

    const panel = this.querySelector(`[data-clc-header-mega-panel="${this.activeMegaId}"]`);
    const trigger = this.querySelector(`[data-clc-header-mega-trigger="${this.activeMegaId}"]`);
    const navItem = trigger?.closest(".clc-header__nav-item");

    panel?.setAttribute("hidden", "");
    trigger?.setAttribute("aria-expanded", "false");
    navItem?.classList.remove("is-open");
    this.activeMegaId = null;
    this.classList.remove("is-mega-open");

    if (unlockScroll && !this.isMobileOpen) {
      this.overlay?.setAttribute("hidden", "");
      this.unlockPageScroll();
    }

    this.updateHeaderMetrics();
  }

  closeAllMenus() {
    this.isMobileOpen = false;
    this.classList.remove("is-mobile-open");
    this.mobilePanel?.setAttribute("aria-hidden", "true");
    this.menuToggle?.setAttribute("aria-expanded", "false");
    this.menuToggle?.setAttribute(
      "aria-label",
      this.menuToggle?.dataset.menuLabelOpen || "Open menu"
    );
    this.closeMegaMenu(false);
    this.overlay?.setAttribute("hidden", "");
    this.unlockPageScroll();
    this.updateHeaderMetrics();
  }

  lockPageScroll() {
    if (document.documentElement.classList.contains("clc-header-nav-open")) {
      return;
    }

    document.documentElement.classList.add("clc-header-nav-open");
  }

  unlockPageScroll() {
    document.documentElement.classList.remove("clc-header-nav-open");
  }
}

if (!customElements.get("clc-new-header")) {
  customElements.define("clc-new-header", ClcNewHeader);
}
